// Phantom Wallet Service Implementation - Based on Admin Page Approach
import { IWalletService } from '../interfaces/IWalletService.js';
import { getConfig } from '../../config/index.js';
import { secureStorage } from '../../utils/security';

/**
 * Simplified Phantom Wallet Service based on working admin page implementation
 * Uses direct Phantom API calls with onlyIfTrusted for persistent connections
 */
export class PhantomWalletService extends IWalletService {
  constructor() {
    super();
    this.config = getConfig();
    this.walletAddress = null;
    this.publicKey = null;
    this.provider = null;
    this.isConnectedState = false;
  }
  
  /**
   * Initialize persistent wallet connection from localStorage
   */
  async initializePersistentConnection() {
    try {
      const savedRaw = secureStorage.getItem('wallet_connection');
      const savedConnection = typeof savedRaw === 'string' ? savedRaw : (savedRaw ? JSON.stringify(savedRaw) : null);
      if (savedConnection) {
        const connectionData = typeof savedRaw === 'string' ? JSON.parse(savedRaw) : savedRaw;
        
        if (connectionData.walletType === 'phantom' && connectionData.address) {
          // Attempt to restore connection silently
          if (this.config.ENABLE_DEBUG_LOGS) {
            console.log('🔄 Attempting to restore Phantom wallet connection:', connectionData.address);
          }
          
          // Try to reconnect silently
          const reconnectResult = await this.silentReconnect();
          if (reconnectResult.success) {
            if (this.config.ENABLE_DEBUG_LOGS) {
              console.log('✅ Successfully restored Phantom wallet connection');
            }
          } else {
            // Clear invalid saved connection
            secureStorage.removeItem('wallet_connection');
            if (this.config.ENABLE_DEBUG_LOGS) {
              console.log('🗑️ Cleared invalid saved wallet connection');
            }
          }
        }
      }
    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ Error initializing persistent connection:', error);
      }
      // Clear corrupted data
      secureStorage.removeItem('wallet_connection');
    }
  }
  
  /**
   * Attempt to reconnect silently without user interaction
   */
  async silentReconnect() {
    try {
      if (!window.solana || !window.solana.isPhantom) {
        if (this.config.ENABLE_DEBUG_LOGS) {
          console.log('❌ PhantomWalletService: Phantom wallet not available for silent reconnect');
        }
        return { success: false, error: 'Phantom wallet not available' };
      }
      
      this.provider = window.solana;
      
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('🔍 PhantomWalletService: Checking wallet connection state...');
        console.log('🔍 Provider isConnected:', this.provider.isConnected);
        console.log('🔍 Provider publicKey:', this.provider.publicKey);
      }
      
      // Check if already connected OR if wallet has public key (common case for persistent wallets)
      if ((this.provider.isConnected && this.provider.publicKey) || this.provider.publicKey) {
        const address = this.provider.publicKey.toString();
        
        // Verify this matches our saved connection
        const savedConnRaw = secureStorage.getItem('wallet_connection');
        if (savedConnRaw) {
          const connectionData = typeof savedConnRaw === 'string' ? JSON.parse(savedConnRaw) : savedConnRaw;
          if (connectionData.address === address) {
            this.walletAddress = address;
            this.publicKey = this.provider.publicKey;
            this.isConnectedState = true;
            
            if (this.config.ENABLE_DEBUG_LOGS) {
              console.log('✅ PhantomWalletService: Wallet public key matches saved connection, restoring state');
            }
            
            return {
              success: true,
              address,
              publicKey: this.publicKey
            };
          } else {
            if (this.config.ENABLE_DEBUG_LOGS) {
              console.log('⚠️ PhantomWalletService: Wallet public key does not match saved connection');
              console.log('Current:', address, 'Saved:', connectionData.address);
            }
          }
        }
      }
      
      // Try to connect silently (Phantom supports this better than Nightly)
      if (this.provider.connect) {
        if (this.config.ENABLE_DEBUG_LOGS) {
          console.log('🔄 PhantomWalletService: Attempting silent connect...');
        }
        
        try {
          const response = await this.provider.connect({ onlyIfTrusted: true });
          
          if (response && response.publicKey) {
            const address = response.publicKey.toString();
            this.walletAddress = address;
            this.publicKey = response.publicKey;
            this.isConnectedState = true;
            
            if (this.config.ENABLE_DEBUG_LOGS) {
              console.log('✅ PhantomWalletService: Silent connect successful');
            }
            
            return {
              success: true,
              address,
              publicKey: this.publicKey
            };
          }
        } catch (connectError) {
          if (this.config.ENABLE_DEBUG_LOGS) {
            console.log('❌ PhantomWalletService: Silent connect failed:', connectError.message);
          }
          // Silent connect failed, this is expected if user hasn't granted persistent permission
        }
      }
      
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('❌ PhantomWalletService: Wallet not connected and silent connect not available');
      }
      return { success: false, error: 'Wallet not connected' };
    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ PhantomWalletService: Error in silent reconnect:', error);
      }
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Save wallet connection to localStorage
   */
  saveConnectionState(address) {
    try {
      const connectionData = {
        walletType: 'phantom',
        address: address,
        timestamp: Date.now()
      };
      secureStorage.setItem('wallet_connection', JSON.stringify(connectionData));
      
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('💾 Saved wallet connection state to secure storage');
      }
    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ Error saving wallet connection state:', error);
      }
    }
  }
  
  /**
   * Clear wallet connection from localStorage
   */
  clearConnectionState() {
    try {
      secureStorage.removeItem('wallet_connection');
      
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('🗑️ Cleared wallet connection state from secure storage');
      }
    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ Error clearing wallet connection state:', error);
      }
    }
  }

  /**
   * Connect to Phantom wallet
   * @param {string} walletType - Should be 'phantom'
   * @returns {Promise<{success: boolean, address?: string, publicKey?: any, error?: string}>}
   */
  async connect(walletType = 'phantom') {
    if (walletType !== 'phantom') {
      return {
        success: false,
        error: `Unsupported wallet type: ${walletType}. Only 'phantom' is supported.`
      };
    }

    try {
      if (!window.solana || !window.solana.isPhantom) {
        return {
          success: false,
          error: 'Phantom wallet not found! Please install Phantom wallet.'
        };
      }

      this.provider = window.solana;
      
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('👻 Attempting to connect to Phantom wallet...');
      }

      const response = await this.provider.connect();
      
      if (!response) {
        return {
          success: false,
          error: 'No response from Phantom wallet'
        };
      }

      let address;
      if (response.publicKey && typeof response.publicKey === 'string') {
        address = response.publicKey;
        this.publicKey = response.publicKey;
      } else if (response.publicKey && typeof response.publicKey.toString === 'function') {
        address = response.publicKey.toString();
        this.publicKey = response.publicKey;
      } else {
        return {
          success: false,
          error: 'Unable to extract public key from Phantom wallet response'
        };
      }

      this.walletAddress = address;
      this.isConnectedState = true;
      
      // Save connection state to localStorage for persistence
      this.saveConnectionState(address);
      
      // Clear disconnect flag since user explicitly connected
      secureStorage.removeItem('wallet_disconnected');

      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('✅ Connected to Phantom wallet:', address);
      }

      return {
        success: true,
        address,
        publicKey: this.publicKey
      };

    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ Error connecting to Phantom wallet:', error);
      }
      
      // Handle user cancellation gracefully
      if (error.message.includes('User rejected') || error.message.includes('cancelled')) {
        return {
          success: false,
          error: 'Connection cancelled by user'
        };
      }
      
      return {
        success: false,
        error: `Failed to connect to Phantom wallet: ${error.message}`
      };
    }
  }

  /**
   * Disconnect from Phantom wallet
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async disconnect() {
    try {
      if (this.provider && this.provider.disconnect) {
        await this.provider.disconnect();
      }

      this.walletAddress = null;
      this.publicKey = null;
      this.provider = null;
      this.isConnectedState = false;
      
      // Clear connection state from localStorage
      this.clearConnectionState();
      
      // Set disconnect flag to prevent auto-reconnect
      const disconnectTimestamp = Date.now().toString();
      secureStorage.setItem('wallet_disconnected', disconnectTimestamp);
      console.log('🔌 DISCONNECT DEBUG: Set disconnect flag:', disconnectTimestamp);

      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('🔌 Disconnected from Phantom wallet and set disconnect flag');
      }

      return {
        success: true
      };

    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ Error disconnecting from Phantom wallet:', error);
      }
      
      // Reset state even if disconnect fails
      this.walletAddress = null;
      this.publicKey = null;
      this.provider = null;
      this.isConnectedState = false;
      
      return {
        success: false,
        error: `Failed to disconnect from Phantom wallet: ${error.message}`
      };
    }
  }

  /**
   * Check if wallet is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.isConnectedState && this.walletAddress !== null;
  }
  
  /**
   * Check for existing wallet connection (for ServiceContext)
   * Uses admin page approach with onlyIfTrusted auto-connect
   * @returns {Promise<{success: boolean, address?: string, publicKey?: any, error?: string}>}
   */
  async checkExistingConnection() {
    try {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('🔍 PhantomWalletService: checkExistingConnection called - AUTO-CONNECT DISABLED FOR EXPLICIT USER CONSENT');
      }
      
      // First check if we already have a connection
      if (this.isConnectedState && this.walletAddress && this.publicKey) {
        if (this.config.ENABLE_DEBUG_LOGS) {
          console.log('✅ PhantomWalletService: Already connected, returning existing connection');
        }
        return {
          success: true,
          address: this.walletAddress,
          publicKey: this.publicKey
        };
      }
      
      // DISABLED: Auto-connection to require explicit user consent
      // This ensures users see the wallet connection screen and must explicitly approve connection
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('🚫 PhantomWalletService: Auto-connect disabled - requiring explicit user action');
      }
      
      return {
        success: false,
        error: 'Auto-connect disabled - explicit user action required'
      };
    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ PhantomWalletService: Error in checkExistingConnection:', error);
      }
      return { success: false, error: error.message };
    }
  }

  /**
   * Get current wallet address
   * @returns {string|null}
   */
  getAddress() {
    return this.walletAddress;
  }

  /**
   * Get current wallet public key
   * @returns {any|null}
   */
  getPublicKey() {
    return this.publicKey;
  }

  /**
   * Sign a transaction
   * @param {any} transaction - Transaction to sign
   * @returns {Promise<any>}
   */
  async signTransaction(transaction) {
    if (!this.isConnected()) {
      throw new Error('Wallet not connected');
    }

    if (!this.provider.signTransaction) {
      throw new Error('Wallet does not support transaction signing');
    }

    try {
      return await this.provider.signTransaction(transaction);
    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ Error signing transaction:', error);
      }
      throw new Error(`Failed to sign transaction: ${error.message}`);
    }
  }

  /**
   * Sign multiple transactions
   * @param {any[]} transactions - Transactions to sign
   * @returns {Promise<any[]>}
   */
  async signAllTransactions(transactions) {
    if (!this.isConnected()) {
      throw new Error('Wallet not connected');
    }

    if (!this.provider.signAllTransactions) {
      throw new Error('Wallet does not support multiple transaction signing');
    }

    try {
      return await this.provider.signAllTransactions(transactions);
    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ Error signing transactions:', error);
      }
      throw new Error(`Failed to sign transactions: ${error.message}`);
    }
  }

  /**
   * Get supported wallet types
   * @returns {string[]}
   */
  getSupportedWallets() {
    return ['phantom'];
  }

  /**
   * Check if wallet is available
   * @param {string} walletType - Type of wallet to check
   * @returns {boolean}
   */
  isWalletAvailable(walletType = 'phantom') {
    if (walletType !== 'phantom') {
      return false;
    }
    return !!(window.solana && window.solana.isPhantom);
  }

  /**
   * Check if wallet is already connected (without triggering connection)
   * @returns {boolean}
   */
  checkConnectionStatus() {
    try {
      if (!this.isWalletAvailable()) {
        return false;
      }

      this.provider = window.solana;
      return !!(this.provider.isConnected && this.provider.publicKey);
    } catch (error) {
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.error('❌ Error checking connection status:', error);
      }
      return false;
    }
  }
}
